import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {deprecatedTrackEvent} from 'analytics/Analytics';
import {INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {schemaQualifiedTableNameToSchemaAndCompleteTableNames} from 'insights/warehouse/WarehouseUtils';
import {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ClipboardText from 'ui-library/ClipboardText';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {AsyncStatus, useAsyncStateOnMount} from 'ui-library/hooks/useAsyncState';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import InfoIcon from 'ui-library/icons/InfoIcon';
import LoadingPaneContents from 'ui-library/pane/LoadingPaneContents';
import Pane from 'ui-library/pane/Pane';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {runUntrustedSQLQuery} from '../AnalyticsWarehouseDAO';
import {LegacyRunQueryState, RunQueryOutput, RunQueryState} from '../AnalyticsWarehouseTypes';
import {convertRunQueryStateToLegacyState} from '../convertRunQueryOutputToLegacyOutput';
import RunQueryOutputTable from '../RunQueryOutputTable';
import {WarehouseUserDefinedViewMetadataFull} from '../warehouseModelsDataLayout';
import OpenEntityDiagramButton from './OpenEntityDiagramButton';
import trackOnCopy from './trackOnCopy';

export interface PresentationalWarehouseTableDetailsProps {
  additionalActionButtons?: JSX.Element;
  fullTableName: string;
  onOpenListing: () => void;
  runQueryState: LegacyRunQueryState;
  userDefinedViewData?: WarehouseUserDefinedViewMetadataFull;
}

export function PresentationalWarehouseTableDetails({
  additionalActionButtons,
  fullTableName,
  onOpenListing,
  runQueryState,
  userDefinedViewData,
}: PresentationalWarehouseTableDetailsProps): JSX.Element {
  useOnMount(() => {
    deprecatedTrackEvent(INSIGHTS_ANALYTICS_ACTIONS.OPENED_TABLE_IN_SCHEMA_BROWSER);
  });

  const [schemaName, completeTableName] =
    schemaQualifiedTableNameToSchemaAndCompleteTableNames(fullTableName);

  const hasDescription = userDefinedViewData?.description != null && userDefinedViewData.description !== '';
  const handleCopyViewQuery = (): void => {
    copyToClipboard(userDefinedViewData!.viewQuery, {
      showToastOnSuccess: true,
      toastText: 'Copied view SQL query to clipboard',
    });
  };
  return (
    <Pane
      title={
        <span className="u-pointer" onClick={onOpenListing}>
          <ClipboardText
            className="u-vertical-center-items"
            clipboardData={fullTableName}
            onClick={trackOnCopy}
          >
            <BenchlingIcon
              className={classNames('u-primary-color', css(styles.titleIcon))}
              icon="chevron-left"
            />
            <span className="u-ellipsis-text">{fullTableName}</span>
            {hasDescription && (
              <span className="u-mini-spacing-left">
                <Popover
                  interaction={FloatingInteraction.HOVER}
                  renderContent={() => <span>{userDefinedViewData.description}</span>}
                  placement={FloatingPlacement.TOP}
                >
                  <InfoIcon />
                </Popover>
              </span>
            )}
          </ClipboardText>
        </span>
      }
      fitContent={true}
      toolbar={
        <>
          <span className={classNames('u-mini-spacing-right', 'faded')}>
            {runQueryState.status === AsyncStatus.SUCCESS
              ? `${runQueryState.data.rows.length} row preview`
              : null}
          </span>
          {userDefinedViewData && (
            <span className={classNames('u-mini-spacing-right', 'faded')}>
              <Tooltip text="Copy view SQL query" placement={FloatingPlacement.TOP}>
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  icon="copy"
                  onClick={handleCopyViewQuery}
                />
              </Tooltip>
            </span>
          )}
          <span className={classNames('u-mini-spacing-right', 'faded')}>
            <OpenEntityDiagramButton
              schemaName={schemaName}
              tableName={completeTableName}
              tooltipPlacement={TooltipPlacement.TOP}
            />
          </span>
          {additionalActionButtons}
        </>
      }
    >
      {runQueryState.status === AsyncStatus.SUCCESS && (
        <RunQueryOutputTable runQueryOutput={runQueryState.data} />
      )}
      {runQueryState.status === AsyncStatus.LOADING && <LoadingPaneContents />}
    </Pane>
  );
}

export default function ConnectedWarehouseTableDetails(
  props: Omit<PresentationalWarehouseTableDetailsProps, 'runQueryState'>
): JSX.Element {
  const runQueryState = useAsyncStateOnMount<RunQueryOutput>(() =>
    // If the future, if parameters support tables, then we could make fullTableName a table
    // parameter instead of interpolating here.
    runUntrustedSQLQuery({
      parameterizedSQLQuery: `SELECT * FROM ${props.fullTableName} LIMIT 5`,
      parameters: [],
    })
  );
  return (
    <PresentationalWarehouseTableDetails
      {...props}
      runQueryState={convertRunQueryStateToLegacyState(runQueryState as RunQueryState)}
    />
  );
}

const styles = StyleSheet.create({
  titleIcon: {
    flexShrink: 0,
  },
});
